import { APIClient } from '../../helpers/api_helper';
import TokenManager from '../../utils/tokenManager';
import SecureApi from '../../utils/secureApi';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;
const api = new APIClient();

export interface ClubListingData {
  _id: string;
  name: string;
  logo: string;
  emailId: string;
  managerName: string;
  countryCode: number;
  managerContactNum: number;
  location: string;
  // Optional fields that might not be in all responses
  isActive?: boolean;
  teamsCount?: number;
  ageCategories?: string[];
  type?: 'club' | 'team';
  createdAt?: string;
  updatedAt?: string;
  // ✅ NEW FIELDS
  address?: string;
  gstNum?: string;  // API uses gstNum
}

export interface ListClubsResponse {
  statusCode: number;
  status: string;
  message: string;
  data: {
    paginatedResults: ClubListingData[];
    totalCount: number;
  };
}

// Add new interface for paginated clubs response
export interface PaginatedClubsResponse {
  clubs: ClubListingData[];
  totalCount: number;
}

// Mock data for testing
const mockClubsData: ClubListingData[] = [
  {
    _id: '1',
    name: 'Phoenix Sports Club',
    logo: '',
    emailId: 'phoenix@example.com',
    managerName: 'Ramesh Kumar',
    countryCode: 91,
    managerContactNum: 9876543210,
    location: 'Magrath Road, Ashok Nagar',
    isActive: true,
    teamsCount: 11,
    ageCategories: ['U11', 'U12', 'U13', 'U14'],
    type: 'club'
  },
  {
    _id: '2',
    name: 'Blaze United FC',
    logo: '',
    emailId: 'blaze@example.com',
    managerName: 'Priya Sharma',
    countryCode: 91,
    managerContactNum: 9876543211,
    location: 'Magrath Road, Ashok Nagar',
    isActive: true,
    teamsCount: 1,
    ageCategories: ['U10'],
    type: 'team'
  },
  {
    _id: '3',
    name: 'Ironclad Warriors',
    logo: '',
    emailId: 'ironclad@example.com',
    managerName: 'Sandeep Varma',
    countryCode: 91,
    managerContactNum: 9876543212,
    location: 'Magrath Road, Ashok Nagar',
    isActive: true,
    teamsCount: 8,
    ageCategories: ['U14-A', 'U14-B'],
    type: 'team'
  },
  {
    _id: '4',
    name: 'Stormbreak Academy',
    logo: '',
    emailId: 'stormbreak@example.com',
    managerName: 'Akash Mehta',
    countryCode: 91,
    managerContactNum: 9876543213,
    location: 'Magrath Road, Ashok Nagar',
    isActive: true,
    teamsCount: 10,
    ageCategories: ['U16'],
    type: 'club'
  },
  {
    _id: '5',
    name: 'Velocity Strikers',
    logo: '',
    emailId: 'velocity@example.com',
    managerName: 'Neha Iyer',
    countryCode: 91,
    managerContactNum: 9876543214,
    location: 'Magrath Road, Ashok Nagar',
    isActive: false,
    teamsCount: 5,
    ageCategories: ['U9'],
    type: 'club'
  },
  {
    _id: '6',
    name: 'Shadow Hawks',
    logo: '',
    emailId: 'shadow@example.com',
    managerName: 'Arjun Desai',
    countryCode: 91,
    managerContactNum: 9876543215,
    location: 'Magrath Road, Ashok Nagar',
    isActive: true,
    teamsCount: 7,
    ageCategories: ['U12', 'U13'],
    type: 'club'
  }
];

// Fetch all clubs for admin/operations team
export const getAllClubs = async (): Promise<ClubListingData[]> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await api.get(`${API_BASE_URL}/api/v1/club/list-clubs`, null);
    
    // Handle the API response structure
    if (response && response.data && response.data.paginatedResults) {
      return response.data.paginatedResults as ClubListingData[];
    }
    
    // Fallback if response structure is different
    if (response && Array.isArray(response)) {
      return response as ClubListingData[];
    }
    
    if (response && response.data && Array.isArray(response.data)) {
      return response.data as ClubListingData[];
    }
    
    // If no data found, return empty array
    if (response && response.data && response.data.paginatedResults === undefined) {
      console.log('No clubs data found in response');
      return [];
    }
    
    // For development/testing, return mock data if API fails
    if (process.env.NODE_ENV === 'development') {
      console.log('Development mode: returning mock data');
      return mockClubsData;
    }
    
    throw new Error('Invalid response structure from clubs API');
  } catch (error) {
    console.error('Failed to fetch all clubs:', error);
    
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
    }
    
    // For development/testing, return mock data as fallback
    if (process.env.NODE_ENV === 'development') {
      console.log('Development mode: returning mock data as fallback');
      return mockClubsData;
    }
    
    throw error;
  }
};

// NEW: Fetch clubs with pagination - no mock data fallback
export const getAllClubsPaginated = async (pageNum: number = 1): Promise<PaginatedClubsResponse> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    
    const response = await api.get(`${API_BASE_URL}/api/v1/club/list-clubs`, { pageNum });
    
    // Handle the API response structure
    if (response && response.data && response.data.paginatedResults !== undefined) {
      return {
        clubs: response.data.paginatedResults as ClubListingData[],
        totalCount: response.data.totalCount || 0
      };
    }
    
    // Fallback if response structure is different but still has data
    if (response && Array.isArray(response)) {
      return {
        clubs: response as ClubListingData[],
        totalCount: response.length
      };
    }
    
    if (response && response.data && Array.isArray(response.data)) {
      return {
        clubs: response.data as ClubListingData[],
        totalCount: response.data.length
      };
    }
    
    // If no data found, return empty result (not an error)
    console.log('No clubs data found in response - returning empty result');
    return {
      clubs: [],
      totalCount: 0
    };
  } catch (error) {
    console.error('Failed to fetch clubs with pagination:', error);
    
    if (typeof error === 'string' && error.includes('Token')) {
      console.error('Token-related error detected. Please check authentication.');
    }
    
    throw error;
  }
};

// NEW: Fetch all clubs without any mock data fallback (strict API-only)
export const getAllClubsStrict = async (): Promise<ClubListingData[]> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await api.get(`${API_BASE_URL}/api/v1/club/list-clubs`, null);
    
    // Handle the API response structure
    if (response && response.data && response.data.paginatedResults) {
      return response.data.paginatedResults as ClubListingData[];
    }
    
    // Fallback if response structure is different
    if (response && Array.isArray(response)) {
      return response as ClubListingData[];
    }
    
    if (response && response.data && Array.isArray(response.data)) {
      return response.data as ClubListingData[];
    }
    
    // If no data found, return empty array (not an error)
    console.log('No clubs data found in response');
    return [];
  } catch (error) {
    console.error('Failed to fetch all clubs (strict):', error);
    throw error;
  }
};

// Delete a club using POST method (consistent with team/player delete operations)
export const deleteClub = async (clubId: string): Promise<void> => {
  try {
    // Use SecureApi POST method (consistent with team and player delete operations)
    await SecureApi.post(`/api/v1/club/delete-club/${clubId}`, {});
  } catch (error: any) {
    console.error('Failed to delete club:', error);
    
    // Extract meaningful error message from API response
    let errorMessage = 'Failed to delete club. Please try again.';
    
    if (error.response?.data) {
      if (typeof error.response.data === 'string') {
        errorMessage = error.response.data;
      } else if (error.response.data.message) {
        errorMessage = error.response.data.message;
      } else if (error.response.data.error) {
        errorMessage = error.response.data.error;
      } else if (error.response.data.title) {
        errorMessage = error.response.data.title;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }
    
    throw new Error(errorMessage);
  }
};

// Get club details by ID
export const getClubById = async (clubId: string): Promise<ClubListingData> => {
  try {
    const authToken = TokenManager.getToken();
    if (!authToken) {
      throw new Error('No authentication token found');
    }
    
    if (!API_BASE_URL) {
      throw new Error('API_BASE_URL not configured');
    }
    
    const response = await api.get(`${API_BASE_URL}/api/v1/club/fetch-club-info/${clubId}`, null);
    
    if (response && response.data) {
      return response.data as ClubListingData;
    }
    
    return response as unknown as ClubListingData;
  } catch (error) {
    console.error('Failed to fetch club details:', error);
    throw error;
  }
}; 